/**
 * Core time operations - pure functions for working with seconds since midnight
 * All functions work with plain integers instead of TimeValue objects
 */

import { TIME_CONSTANTS } from "../constants";

/**
 * Calculates the difference between two times in seconds
 * @param {number} endSeconds - End time in seconds since midnight
 * @param {number} startSeconds - Start time in seconds since midnight
 * @returns {number} Difference in seconds
 */
export const differenceInSeconds = (endSeconds, startSeconds) => {
  return endSeconds - startSeconds;
};

/**
 * Calculates the difference between two times in hours
 * @param {number} endSeconds - End time in seconds since midnight
 * @param {number} startSeconds - Start time in seconds since midnight
 * @returns {number} Difference in hours (decimal)
 */
export const differenceInHours = (endSeconds, startSeconds) => {
  return (
    differenceInSeconds(endSeconds, startSeconds) /
    TIME_CONSTANTS.SECONDS_PER_HOUR
  );
};

/**
 * Check if time crosses midnight (is overnight)
 * @param {number} seconds - Seconds since midnight
 * @returns {boolean} True if time is >= 24 hours
 */
export const isOvernightTime = (seconds) => {
  return seconds >= TIME_CONSTANTS.SECONDS_PER_DAY;
};

/**
 * Converts seconds since midnight to 12-hour time components
 * @param {number} seconds - Seconds since midnight
 * @returns {{ hour: number, minute: number, period: string }} 12-hour time parts
 */
export const secondsToTime12Hour = (seconds) => {
  const displaySeconds = seconds % TIME_CONSTANTS.SECONDS_PER_DAY;
  const totalMinutes = Math.floor(
    displaySeconds / TIME_CONSTANTS.SECONDS_PER_MINUTE
  );
  const hours24 = Math.floor(totalMinutes / 60);
  const minute = totalMinutes % 60;

  if (hours24 === 0) return { hour: 12, minute, period: "AM" };
  if (hours24 === 12) return { hour: 12, minute, period: "PM" };
  if (hours24 > 12) return { hour: hours24 - 12, minute, period: "PM" };
  return { hour: hours24, minute, period: "AM" };
};

/**
 * Converts 12-hour time components to seconds since midnight
 * @param {number} hour - Hour (1-12)
 * @param {number} minute - Minute (0-59)
 * @param {string} period - "AM" or "PM"
 * @returns {number} Seconds since midnight
 */
export const time12HourToSeconds = (hour, minute, period) => {
  let hours24;
  if (period === "PM" && hour !== 12) {
    hours24 = hour + 12;
  } else if (period === "AM" && hour === 12) {
    hours24 = 0;
  } else {
    hours24 = hour;
  }

  return (
    hours24 * TIME_CONSTANTS.SECONDS_PER_HOUR +
    minute * TIME_CONSTANTS.SECONDS_PER_MINUTE
  );
};

/**
 * Converts hours and minutes to a decimal hour value
 * @param {number} hours - The hours component
 * @param {number} minutes - The minutes component
 * @returns {number} The decimal hour value
 */
export const hoursMinutesToDecimal = (hours, minutes) => {
  const h = parseInt(hours, 10) || 0;
  const m = parseInt(minutes, 10) || 0;
  return h + m / 60;
};

/**
 * Converts a decimal hour value to hours and minutes
 * @param {number} decimalHours - The decimal hour value
 * @returns {{ hours: number, minutes: number }} Object with hours and minutes
 */
export const decimalToHoursMinutes = (decimalHours) => {
  if (!decimalHours && decimalHours !== 0) {
    return { hours: 0, minutes: 0 };
  }

  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);

  return { hours, minutes };
};
